import type { FC, ReactNode } from "react";

import { formatAdminPermissionLabel } from "./adminPermissionCopy";

import { adminUiCopy } from "@/copy/adminUiCopy";
import type { AdminV2RevenueMenuVisibilityKey } from "@/types/adminV2";
import { AdminVNextShell } from "@/vnext/app-shell/AdminVNextShell";
import { VNextCard, VNextNotice } from "@/vnext/primitives";
import "@/vnext/theme/vnext.css";

export const MENU_VISIBILITY_COPY_LABELS: Record<
  AdminV2RevenueMenuVisibilityKey,
  string
> = {
  revenue: adminUiCopy.routeGateSummary.workspaceVisibilityRevenue,
  learning: adminUiCopy.routeGateSummary.workspaceVisibilityLearning,
  sales: adminUiCopy.routeGateSummary.workspaceVisibilitySales,
};

export const formatAvailabilityText = (
  value: string | null | undefined,
): string =>
  value && value.trim().length > 0 ? value : adminUiCopy.common.notAvailable;

export const formatRoleLabels = (args: {
  roleNames: string[];
  bootstrapRoleAssigned: boolean;
  adminE2eBypassEnabled?: boolean;
}): string[] => {
  const roleLabels = [...args.roleNames];

  if (args.bootstrapRoleAssigned) {
    roleLabels.unshift(adminUiCopy.routeGateSummary.bootstrapAdmin);
  }

  if (roleLabels.length > 0) {
    return roleLabels;
  }

  if (args.adminE2eBypassEnabled) {
    return [adminUiCopy.routeGateSummary.e2eBypassRole];
  }

  return [adminUiCopy.routeGateSummary.noStaffRolesAssigned];
};

export const formatVisibleAccessLabels = (
  menuVisibility: Partial<Record<AdminV2RevenueMenuVisibilityKey, boolean>>,
): string[] =>
  (
    Object.entries(menuVisibility) as Array<
      [AdminV2RevenueMenuVisibilityKey, boolean | undefined]
    >
  )
    .filter(([, isEnabled]) => Boolean(isEnabled))
    .map(([key]) => MENU_VISIBILITY_COPY_LABELS[key])
    .sort((left, right) => left.localeCompare(right));

export const RouteGatePageShell: FC<{
  title: string;
  subtitle?: string;
  hideAccessDataErrorBanner?: boolean;
  children: ReactNode;
}> = ({ title, subtitle, hideAccessDataErrorBanner, children }) => (
  <AdminVNextShell
    hideAccessDataErrorBanner={hideAccessDataErrorBanner}
    subtitle={subtitle}
    title={title}
  >
    {children}
  </AdminVNextShell>
);

export const RouteGateShellSurface: FC<{ children: ReactNode }> = ({
  children,
}) => <section className="vnext-gate-surface">{children}</section>;

export const RouteGateCard: FC<{
  title: string;
  alertSeverity: "warning" | "error";
  alertMessage: string;
  maxWidth?: number;
  embedded?: boolean;
  children?: ReactNode;
}> = ({
  title,
  alertSeverity,
  alertMessage,
  maxWidth = 640,
  embedded = false,
  children,
}) => {
  const tone = alertSeverity === "error" ? "danger" : "warning";

  return (
    <div
      className={
        embedded
          ? "vnext-gate-card-shell vnext-gate-card-shell--embedded"
          : "vnext-blocking-shell"
      }
    >
      <VNextCard
        className="vnext-gate-card"
        style={{ maxWidth, width: "100%" }}
        title={title}
      >
        <VNextNotice tone={tone}>{alertMessage}</VNextNotice>
        {children}
      </VNextCard>
    </div>
  );
};

interface RouteGateAccessSummaryProps {
  currentUserName: string;
  currentUserEmail: string;
  currentUserUid: string;
  currentUserStatus: string;
  roleLabels: string[];
  permissionKeys: string[];
  visibleAccessLabels: string[];
  blockedVisibility?: AdminV2RevenueMenuVisibilityKey;
  visibilityScopeLabel?: string;
  visibilityCheckLabels?: string[];
  requestedRoute?: string;
  adminE2eBypassEnabled?: boolean;
}

const RouteGateDetailSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="vnext-gate-details__section">
    <h4 className="vnext-gate-details__title">{title}</h4>
    <div className="vnext-gate-details__body">{children}</div>
  </section>
);

const RouteGateDetailRow = ({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) => (
  <div className="vnext-gate-details__row">
    <span className="vnext-gate-details__label">{label}</span>
    <span className="vnext-gate-details__value">{value}</span>
  </div>
);

export const RouteGateAccessSummary: FC<RouteGateAccessSummaryProps> = ({
  currentUserName,
  currentUserEmail,
  currentUserUid,
  currentUserStatus,
  roleLabels,
  permissionKeys,
  visibleAccessLabels,
  blockedVisibility,
  visibilityScopeLabel,
  visibilityCheckLabels,
  requestedRoute,
  adminE2eBypassEnabled = false,
}) => (
  <details className="vnext-gate-details">
    <summary className="vnext-gate-details__summary">
      <span className="vnext-gate-details__summary-title">
        {adminUiCopy.routeAccess.accessSummaryTitle}
      </span>
      <span className="vnext-gate-details__summary-copy">
        {adminUiCopy.routeAccess.accessSummaryCopy}
      </span>
    </summary>

    <div className="vnext-gate-details__content">
      <RouteGateDetailSection
        title={adminUiCopy.routeGateSummary.signedInAccount}
      >
        <RouteGateDetailRow
          label={adminUiCopy.routeGateSummary.nameLabel}
          value={currentUserName}
        />
        <RouteGateDetailRow
          label={adminUiCopy.routeGateSummary.emailLabel}
          value={currentUserEmail}
        />
        <RouteGateDetailRow
          label={adminUiCopy.routeGateSummary.userIdLabel}
          value={<code>{currentUserUid}</code>}
        />
        <RouteGateDetailRow
          label={adminUiCopy.routeGateSummary.statusLabel}
          value={currentUserStatus}
        />
      </RouteGateDetailSection>

      <RouteGateDetailSection title={adminUiCopy.routeGateSummary.rolesTitle}>
        <ul className="vnext-gate-details__list">
          {roleLabels.map((roleLabel) => (
            <li key={roleLabel}>{roleLabel}</li>
          ))}
        </ul>
      </RouteGateDetailSection>

      <RouteGateDetailSection title={adminUiCopy.routeGateSummary.accessTitle}>
        <RouteGateDetailRow
          label={`${adminUiCopy.routeGateSummary.permissionsLabel} (${permissionKeys.length})`}
          value={
            permissionKeys.length > 0
              ? permissionKeys
                  .map(formatAdminPermissionLabel)
                  .sort((left, right) => left.localeCompare(right))
                  .join(", ")
              : adminUiCopy.routeGateSummary.noneLoadedYet
          }
        />
        <RouteGateDetailRow
          label={adminUiCopy.routeGateSummary.visibleWorkspacesLabel}
          value={
            visibleAccessLabels.length > 0
              ? visibleAccessLabels.join(", ")
              : visibilityScopeLabel
                ? adminUiCopy.routeGateSummary.visibleWorkspacesScopedEmpty
                : adminUiCopy.routeGateSummary.noneEnabled
          }
        />
        {blockedVisibility ? (
          <RouteGateDetailRow
            label={adminUiCopy.routeGateSummary.hiddenWorkspaceLabel}
            value={
              <code>{MENU_VISIBILITY_COPY_LABELS[blockedVisibility]}</code>
            }
          />
        ) : null}
        {visibilityScopeLabel ? (
          <RouteGateDetailRow
            label={adminUiCopy.routeGateSummary.visibilityScopeLabel}
            value={<code>{visibilityScopeLabel}</code>}
          />
        ) : null}
        {visibilityCheckLabels && visibilityCheckLabels.length > 0 ? (
          <RouteGateDetailRow
            label={adminUiCopy.routeGateSummary.visibilityChecksLabel}
            value={visibilityCheckLabels.join(", ")}
          />
        ) : null}
        {requestedRoute ? (
          <RouteGateDetailRow
            label={adminUiCopy.routeGateSummary.requestedRouteLabel}
            value={<code>{requestedRoute}</code>}
          />
        ) : null}
        {adminE2eBypassEnabled ? (
          <div className="vnext-gate-details__meta">
            {adminUiCopy.routeGateSummary.e2eBypassNotice}
          </div>
        ) : null}
      </RouteGateDetailSection>
    </div>
  </details>
);
